import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { ChevronRight, ArrowRight } from 'lucide-react';
const examples = [{
  title: 'Repository secret → cloud role → production data',
  chain: ['Repository secret', 'Cloud role', 'Production data'],
  body: 'A credential exposure in source code creates access to cloud resources tied to production systems. Periscan shows the source, impact, remediation, rotation guidance, and verification step.'
}, {
  title: 'Identity misconfiguration → privileged access',
  chain: ['Identity misconfig', 'Excessive perms', 'Privileged access'],
  body: 'A conditional access gap, excessive permission, stale admin relationship, or identity path creates privileged access risk. Periscan explains the chain and how to break it.'
}, {
  title: 'External exposure → vulnerable service → sensitive system',
  chain: ['Internet-facing asset', 'Vulnerable service', 'Sensitive system'],
  body: 'An internet-facing asset creates a reachable path toward a high-value system. Periscan validates reachability, business context, and remediation priority.'
}, {
  title: 'AI prompt injection → restricted data retrieval',
  chain: ['User input', 'Prompt injection', 'Restricted data'],
  body: 'An AI app, RAG system, or agent can be manipulated to access data outside the intended policy. Periscan tests the misuse path and produces AI-security evidence.'
}, {
  title: 'Closed ticket → still-exposed risk',
  chain: ['Ticket closed', 'Validation re-run', 'Still exposed'],
  body: 'A remediation ticket was closed, but the exposure still exists or reappeared. Periscan re-tests and flags the gap between activity and actual risk reduction.'
}];
export function AttackPathExamples() {
  const [active, setActive] = useState(0);
  return <section className="relative w-full py-32 border-t border-white/5" data-id="element-264">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-265">
        <SectionLabel number="09" label="Example Paths" className="mb-8" data-id="element-266" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12" data-id="element-267">
          <div className="lg:col-span-8" data-id="element-268">
            <SectionHeading size="xl" data-id="element-269">
              Paths Periscan
              <br data-id="element-270" />
              <span className="text-white/40" data-id="element-271">can identify.</span>
            </SectionHeading>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-272">
          {/* List */}
          <div className="lg:col-span-5" data-id="element-273">
            <div className="space-y-px bg-white/5 rounded-2xl overflow-hidden border border-white/5" data-id="element-274">
              {examples.map((ex, i) => <button key={i} onClick={() => setActive(i)} className={`w-full text-left px-6 py-5 transition-colors flex items-center gap-4 ${active === i ? 'bg-[#1a8fd4]/10' : 'bg-[#070a0e] hover:bg-[#0a0d12]'}`} data-id="element-275">
                  <span className={`font-mono text-xs ${active === i ? 'text-[#1a8fd4]' : 'text-white/30'}`} data-id="element-276">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className={`text-sm font-medium flex-1 ${active === i ? 'text-white' : 'text-white/60'}`} data-id="element-277">
                    {ex.title}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-all ${active === i ? 'text-[#1a8fd4] translate-x-1' : 'text-white/20'}`} data-id="element-278" />
                </button>)}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7" data-id="element-279">
            <AnimatePresence mode="wait" data-id="element-280">
              <motion.div key={active} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} className="rounded-2xl bg-[#0a0d12] border border-white/5 p-8 md:p-12" data-id="element-281">
                {/* Chain visualization */}
                <div className="mb-8" data-id="element-282">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#1a8fd4] mb-6" data-id="element-283">
                    Attack Path
                  </p>
                  <div className="flex flex-wrap items-center gap-3" data-id="element-284">
                    {examples[active].chain.map((node, i) => <Fragment key={i} data-id="element-285">
                        <div className="px-4 py-2.5 rounded-lg bg-[#1a8fd4]/10 border border-[#1a8fd4]/30" data-id="element-286">
                          <span className="font-mono text-xs text-white" data-id="element-287">
                            {node}
                          </span>
                        </div>
                        {i < examples[active].chain.length - 1 && <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: 'auto'
                    }} transition={{
                      duration: 0.5,
                      delay: i * 0.15
                    }} className="flex items-center" data-id="element-288">
                            <ArrowRight className="w-4 h-4 text-[#1a8fd4]/60" data-id="element-289" />
                          </motion.div>}
                      </Fragment>)}
                  </div>
                </div>

                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4 tracking-tight leading-tight" data-id="element-290">
                  {examples[active].title}
                </h3>
                <p className="text-base text-white/60 leading-relaxed" data-id="element-291">
                  {examples[active].body}
                </p>

                {/* Output note */}
                <div className="mt-8 pt-8 border-t border-white/5" data-id="element-292">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40" data-id="element-293">
                    Output: evidence, remediation steps, and re-test
                    verification.
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}